/**
 * FarmRoadHybridService
 *
 * Service d'orchestration pour l'analyse agricole intelligente.
 * Combine les mesures PostgreSQL, les seuils configurés et l'agent Aladinn.
 *
 * Fonctionnalités :
 * - Récupération des données de mesures et seuils
 * - Agrégation intelligente selon la période d'analyse
 * - Génération de diagnostics et recommandations par IA
 * - Analyse à la demande (pas de cache)
 */

import FarmRoadInsightsService from './FarmRoadInsightsService';
import { getFirebaseToken } from '../firebase';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';


class FarmRoadHybridService {
  constructor() {
    this.analysisCache = new Map(); // Cache temporaire pour la session uniquement
  }

  // =========================================================================
  // ANALYSE HYBRIDE COMPLÈTE
  // =========================================================================

  /**
   * Effectue une analyse complète (mesures + seuils + IA)
   * NOUVELLE VERSION : Appelle l'API backend centralisée
   * @param {Object} params - Paramètres d'analyse
   * @returns {Promise<Object>} Résultat de l'analyse
   */
  async performHybridAnalysis(params) {
    const {
      farmId,
      farmName,
      culture,
      objective,
      period, // '24h', 'daily', 'weekly', 'monthly', '3months'
      sectorId, // Optionnel : filtrer par secteur
      startDate,
      endDate
    } = params;

    console.log('[FarmRoadHybridService] Démarrage analyse via API centralisée:', {
      farmId,
      period,
      sectorId,
      culture,
      objective
    });

    try {
      // 1. Déterminer les dates selon la période
      const { start, end } = this._determinePeriodDates(period, startDate, endDate);

      // 2. Récupérer le token Firebase pour l'authentification
      const token = await getFirebaseToken();

      if (!token) {
        throw new Error('Impossible de récupérer le token d\'authentification');
      }

      // 3. Appeler l'API backend centralisée
      console.log('[FarmRoadHybridService] Appel API /farm-analysis/complete...');
      const response = await fetch(`${BackEndUrl}/api/farm-analysis/complete/${farmId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sectorId,
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          culture: culture || 'framboises',
          objective: objective || null
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur API (${response.status}): ${errorText}`);
      }

      const apiResult = await response.json();

      if (!apiResult.success) {
        throw new Error(apiResult.error || 'Analyse échouée');
      }

      console.log('[FarmRoadHybridService] ✓ Analyse reçue du backend');

      // 4. Transformer la réponse API au format attendu par le frontend
      const result = {
        success: true,
        timestamp: new Date().toISOString(),
        metadata: {
          farmId,
          farmName,
          sectorId: sectorId || null,
          sectorName: sectorId ? `Secteur ${sectorId}` : 'Toute la ferme',
          culture,
          objective,
          period: {
            label: period,
            start: start.toISOString(),
            end: end.toISOString(),
            duration: ((end - start) / (1000 * 60 * 60 * 24)).toFixed(1) + ' jours'
          }
        },
        data: {
          thresholdsEvaluation: apiResult.data.evaluation?.byIndicator || {},
          dailyIndicators: apiResult.data.indicators || {},
          dailyEvaluation: apiResult.data.evaluation || {}
        },
        analysis: {
          raw: apiResult.data.analysis,
          parsed: {
            sections: apiResult.data.analysis
          }
        },
        // Données supplémentaires de l'API
        measurementsCount: apiResult.data.measurementsCount,
        insightId: apiResult.data.insightId
      };

      console.log('[FarmRoadHybridService] ✓ Analyse hybride terminée avec succès (via API)');

      return result;

    } catch (error) {
      console.error('[FarmRoadHybridService] ❌ Erreur lors de l\'analyse hybride:', error);
      return {
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  // =========================================================================
  // CONSTRUCTION DU PROMPT POUR ALADINN
  // =========================================================================

  /**
   * Construit le prompt enrichi pour l'analyse par Aladinn
   * @param {Object} data - Données contextuelles
   * @returns {string} Prompt formaté
   */
  _buildAnalysisPrompt(data) {
    const {
      analyticsReport,
      thresholdsEvaluation,
      dailyIndicators,
      dailyEvaluation,
      objective,
      culture,
      period,
      periodType, // '24h', 'daily', 'weekly', etc.
      sectorId,
      sectorName,
      aggregatedData,
      strategyInfo
    } = data;

    // Fonction helper pour formater les séries temporelles
    const formatTimeSeries = (data) => {
      if (!data || !data.intervals || data.intervals.length === 0) return '';

      // Prendre les 12 dernières valeurs pour montrer l'évolution récente
      const recentIntervals = data.intervals.slice(-12);
      const timeSeriesText = recentIntervals.map(interval => {
        const time = new Date(interval.timestamp).toLocaleString('fr-FR', {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
        return `  ${time}: ${interval.mean.toFixed(2)} ${interval.unit} (min: ${interval.min.toFixed(2)}, max: ${interval.max.toFixed(2)})`;
      }).join('\n');

      return `\n**Évolution récente (${recentIntervals.length} dernières mesures)**:\n${timeSeriesText}`;
    };

    // Déterminer le contexte de saison et phase de croissance (climat Maroc)
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-11
    const currentDay = now.getDate();
    let season = '';
    let growthPhaseHint = '';

    // Saisons ajustées pour le climat marocain avec transitions
    if (currentMonth >= 3 && currentMonth <= 5) {
      // Avril-Juin: Printemps
      season = 'printemps';
      growthPhaseHint = 'Phase probable: reprise végétative, floraison précoce';
    } else if (currentMonth >= 6 && currentMonth <= 8) {
      // Juillet-Septembre: Été
      season = 'été';
      growthPhaseHint = 'Phase probable: floraison, fructification, récolte';
    } else if (currentMonth >= 9 && currentMonth <= 11) {
      // Octobre-Décembre: Automne
      season = 'automne';
      growthPhaseHint = 'Phase probable: fin de fructification, préparation dormance';
    } else if (currentMonth === 2) {
      // Mars: Transition hiver → printemps
      season = 'fin d\'hiver (transition vers printemps)';
      growthPhaseHint = 'Phase probable: sortie de dormance, début reprise végétative';
    } else {
      // Janvier-Février: Hiver
      season = 'hiver';
      if (currentMonth === 1 && currentDay >= 15) {
        // Mi-février: proche de la fin d'hiver
        growthPhaseHint = 'Phase probable: fin de dormance, préparation reprise végétative';
      } else {
        growthPhaseHint = 'Phase probable: dormance ou culture forcée sous serre';
      }
    }

    return `Tu es un expert en agriculture de précision et en agronomie. Je te fournis des données de mesures agricoles d'une exploitation sous serre et j'ai besoin de ton analyse experte.

# CONTEXTE DE L'EXPLOITATION

**Culture**: ${culture}
**Objectif agricole**: ${objective}
${sectorId ? `**Périmètre d'analyse**: ${sectorName} (analyse ciblée sur un secteur spécifique)` : `**Périmètre d'analyse**: Toute la ferme`}
**Saison actuelle**: ${season} (${growthPhaseHint})
**Période d'analyse**: ${period}
**Stratégie d'agrégation**: ${strategyInfo.name} (échantillonnage tous les ${strategyInfo.name === 'hourly' ? '1h' : strategyInfo.name === 'tri-hourly' ? '3h' : strategyInfo.name === 'daily' ? '24h' : '7 jours'})
**Date d'analyse**: ${analyticsReport.metadata.analysisDate}

**Contexte opérationnel**:
- Type d'exploitation: Culture sous serre
${sectorId ? `- L'analyse est ciblée sur ${sectorName}, les conditions peuvent varier d'un secteur à l'autre` : `- Les mesures couvrent l'ensemble de la ferme`}
- Les mesures reflètent les conditions environnementales contrôlées
- Les variations jour/nuit sont importantes à considérer pour cette culture

---

# 🌟 INDICATEURS POUR L'ANALYSE

${periodType === 'daily' ? `
**MODE D'ANALYSE : SITUATION INSTANTANÉE**

Tu analyses la situation **actuelle** de la serre (période depuis minuit aujourd'hui).

**Instructions importantes** :
- Concentre-toi sur les **valeurs instantanées** (dernières mesures) présentées dans la section "MESURES INSTANTANÉES" ci-dessous
- Analyse les conditions **actuelles** pour identifier les actions immédiates si nécessaire
- Évalue si les paramètres sont dans les zones optimales **en ce moment**
- ⚠️ **N'analyse PAS les indicateurs 24h** pour une situation instantanée (ils ne sont pas pertinents)

` : `
**MODE D'ANALYSE : TENDANCES SUR 24H ET PLUS**

Tu analyses les **tendances et moyennes** sur la période : **${period}**.

**Instructions importantes** :
- Base ton diagnostic sur les **indicateurs agronomiques 24h** ci-dessous (DLI, VPD diurne, températures min/max/moyenne)
- Ces indicateurs reflètent les conditions sur 24 heures, bien plus pertinents qu'une valeur ponctuelle
- Analyse les amplitudes et variations jour/nuit
- Évalue la cohérence des conditions sur la période
- ⚠️ **Les valeurs instantanées sont secondaires**, utilise-les uniquement comme complément d'information

---

## 🎯 HIÉRARCHIE D'ANALYSE (par ordre de priorité)

### 🔴 PRIORITÉ 1 - Facteurs limitants (analyser en PREMIER)
Ces facteurs limitent directement la croissance et nécessitent une action immédiate :

1. **DLI < 12 mol/m²/jour** → Photosynthèse limitée
   - Action : Envisager éclairage d'appoint si persistant
   - Impact : -30% à -50% de croissance

2. **VPD diurne < 0.5 kPa** → Risque maladies fongiques élevé
   - Action : Améliorer ventilation, réduire humidité
   - Impact : Risque botrytis, oïdium dans les 48-72h

3. **VPD diurne > 1.5 kPa** → Stress hydrique sévère
   - Action : Augmenter humidification, réduire température
   - Impact : Fermeture des stomates, photosynthèse arrêtée

4. **Température min < 10°C** → Croissance arrêtée
   - Action : Chauffage requis immédiatement
   - Impact : Arrêt complet de la croissance, risque dommages cellulaires

5. **CO2 diurne < 600 ppm** → Photosynthèse bridée
   - Action : Enrichissement CO2 nécessaire
   - Impact : Photosynthèse limitée à 70% du potentiel maximum

### 🟡 PRIORITÉ 2 - Facteurs d'optimisation
Ces facteurs affectent la qualité et le rendement à moyen terme :

1. **Amplitude thermique jour/nuit** (optimal 8-12°C)
   - Impact sur qualité des fruits et accumulation des sucres

2. **Température moyenne 24h** (cible 18-20°C)
   - Équilibre croissance végétative / reproduction

3. **Humidité substrat stable** (amplitude < 20%)
   - Stress hydrique si variations excessives

### 🟢 PRIORITÉ 3 - Facteurs de performance
Affinent les conditions pour production maximale :

1. **VPD optimal** (0.8-1.2 kPa) → Transpiration efficace
2. **DLI optimal** (15-18 mol/m²/jour) → Production maximale
3. **CO2 optimal** (800-1200 ppm) → Photosynthèse maximale

---

## 🔗 INTERACTIONS CLÉS À ANALYSER

### Interaction VPD - Température - Humidité
**VPD = f(Température, Humidité relative)**

- **Si VPD élevé + Température élevée** → Stress combiné
  - Priorité : Refroidissement d'abord (impact plus rapide)
  - Puis humidification si nécessaire

- **Si VPD faible + Humidité élevée** → Risque fongique
  - Priorité : Ventilation urgente
  - Éviter chauffage seul (aggrave humidité relative)

### Interaction Photosynthèse - Lumière - CO2 - Température
**Photosynthèse = f(Lumière, CO2, Température)**

- **Si DLI bas + CO2 élevé** → CO2 gaspillé
  - Pas d'intérêt à enrichir en CO2
  - Focus sur augmentation lumière

- **Si DLI optimal + CO2 bas** → Goulot CO2
  - Enrichissement CO2 très bénéfique
  - Gain potentiel : +20-30% photosynthèse

- **Si Température basse + Lumière élevée** → Déséquilibre
  - Photosynthèse ralentie malgré lumière disponible
  - Priorité : Augmenter température à 18-20°C

### Interaction Amplitude CO2 - DLI
**Amplitude CO2 élevée indique épuisement rapide**

- **Si Amplitude > 400 ppm + DLI élevé** → Consommation rapide normale
  - Besoin : Enrichissement CO2 pour soutenir photosynthèse

- **Si Amplitude > 400 ppm + DLI faible** → Problème ventilation
  - Vérifier renouvellement d'air

---

## 📊 INDICATEURS AGRONOMIQUES SUR 24H

${dailyIndicators && dailyEvaluation ? `
## 💡 Lumière et Photosynthèse

### DLI (Daily Light Integral)
- **Valeur**: ${dailyIndicators.light?.dli || 'N/A'} mol/m²/jour
- **Statut**: ${dailyEvaluation.byIndicator?.dli ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.dli.severity)} ${dailyEvaluation.byIndicator.dli.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.dli?.threshold?.optimal || 'Non configurée'}
- **Impact**: Le DLI est l'indicateur clé de la photosynthèse cumulée sur 24h, déterminant pour la croissance et la fructification
${dailyEvaluation.byIndicator?.dli?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.dli.deviation.toFixed(2)} mol/m²/jour` : ''}

### Durée d'ensoleillement effectif
- **Valeur**: ${dailyIndicators.light?.sunlightDuration || 'N/A'} heures
- **Statut**: ${dailyEvaluation.byIndicator?.sunlight_duration ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.sunlight_duration.severity)} ${dailyEvaluation.byIndicator.sunlight_duration.message}` : 'Non évalué'}
- **Impact**: Nombre d'heures avec PAR > 100 µmol/m²/s (seuil de photosynthèse)

### Radiation moyenne diurne (6h-20h)
- **Valeur**: ${dailyIndicators.light?.meanDiurnalRadiation || 'N/A'} µmol/m²/s
- **Statut**: ${dailyEvaluation.byIndicator?.radiation_mean_diurnal ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.radiation_mean_diurnal.severity)} ${dailyEvaluation.byIndicator.radiation_mean_diurnal.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.radiation_mean_diurnal?.threshold?.optimal || 'Non configurée'}
- **Impact**: Intensité lumineuse moyenne reçue par les plantes pendant les heures de jour. Complète le DLI (quantité totale) et la durée (photopériode). Important pour évaluer le stress lumineux et l'efficacité photosynthétique.
${dailyEvaluation.byIndicator?.radiation_mean_diurnal?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.radiation_mean_diurnal.deviation.toFixed(2)} µmol/m²/s` : ''}

### Pic d'intensité lumineuse
- **Valeur**: ${dailyIndicators.light?.peakIntensity || 'N/A'} µmol/m²/s

## 🌡️ Température sur 24h

### Température moyenne 24h
- **Valeur**: ${dailyIndicators.temperature?.mean24h || 'N/A'}°C
- **Statut**: ${dailyEvaluation.byIndicator?.temperature_mean ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.temperature_mean.severity)} ${dailyEvaluation.byIndicator.temperature_mean.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.temperature_mean?.threshold?.optimal || 'Non configurée'}
${dailyEvaluation.byIndicator?.temperature_mean?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.temperature_mean.deviation.toFixed(2)}°C` : ''}

### Température minimale 24h
- **Valeur**: ${dailyIndicators.temperature?.min || 'N/A'}°C
- **Statut**: ${dailyEvaluation.byIndicator?.temperature_min ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.temperature_min.severity)} ${dailyEvaluation.byIndicator.temperature_min.message}` : 'Non évalué'}
- **Impact critique**: Risque de stress froid si trop basse
${dailyEvaluation.byIndicator?.temperature_min?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.temperature_min.deviation.toFixed(2)}°C` : ''}

### Température maximale 24h
- **Valeur**: ${dailyIndicators.temperature?.max || 'N/A'}°C
- **Statut**: ${dailyEvaluation.byIndicator?.temperature_max ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.temperature_max.severity)} ${dailyEvaluation.byIndicator.temperature_max.message}` : 'Non évalué'}
- **Impact critique**: Risque de stress thermique si trop élevée
${dailyEvaluation.byIndicator?.temperature_max?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.temperature_max.deviation.toFixed(2)}°C` : ''}

### Amplitude thermique 24h
- **Valeur**: ${dailyIndicators.temperature?.amplitude || 'N/A'}°C
- **Statut**: ${dailyEvaluation.byIndicator?.temperature_amplitude ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.temperature_amplitude.severity)} ${dailyEvaluation.byIndicator.temperature_amplitude.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.temperature_amplitude?.threshold?.optimal || 'Non configurée'}
- **Impact**: L'amplitude jour/nuit est importante pour la qualité des fruits et l'équilibre métabolique
${dailyEvaluation.byIndicator?.temperature_amplitude?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.temperature_amplitude.deviation.toFixed(2)}°C` : ''}

### Stress thermique (durée exposition >30°C)
- **Valeur**: ${dailyIndicators.temperature?.heatStressHours || 'N/A'} heures
- **Statut**: ${dailyEvaluation.byIndicator?.temperature_heat_stress ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.temperature_heat_stress.severity)} ${dailyEvaluation.byIndicator.temperature_heat_stress.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.temperature_heat_stress?.threshold?.optimal || 'Non configurée'}
- **Impact critique**: Au-delà de 30°C, la photosynthèse est bloquée et les plantes subissent un stress thermique important
${dailyEvaluation.byIndicator?.temperature_heat_stress?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.temperature_heat_stress.deviation.toFixed(2)} heures` : ''}

### Stress froid (durée exposition <10°C)
- **Valeur**: ${dailyIndicators.temperature?.coldStressHours || 'N/A'} heures
- **Statut**: ${dailyEvaluation.byIndicator?.temperature_cold_stress ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.temperature_cold_stress.severity)} ${dailyEvaluation.byIndicator.temperature_cold_stress.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.temperature_cold_stress?.threshold?.optimal || 'Non configurée'}
- **Impact critique**: En dessous de 10°C, la photosynthèse est ralentie et la croissance peut s'arrêter
${dailyEvaluation.byIndicator?.temperature_cold_stress?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.temperature_cold_stress.deviation.toFixed(2)} heures` : ''}

### Températures diurne/nocturne
- **Température diurne moyenne (6h-20h)**: ${dailyIndicators.temperature?.diurnalMean || 'N/A'}°C
- **Température nocturne moyenne**: ${dailyIndicators.temperature?.nocturnalMean || 'N/A'}°C

## 💧 VPD (Vapor Pressure Deficit) et Climat

### VPD diurne moyen (6h-20h)
- **Valeur**: ${dailyIndicators.vpd?.diurnalMean || 'N/A'} kPa
- **Statut**: ${dailyEvaluation.byIndicator?.vpd_diurnal ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.vpd_diurnal.severity)} ${dailyEvaluation.byIndicator.vpd_diurnal.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.vpd_diurnal?.threshold?.optimal || 'Non configurée'}
- **Impact**: Contrôle la transpiration, l'absorption des nutriments et le risque de maladies fongiques
${dailyEvaluation.byIndicator?.vpd_diurnal?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.vpd_diurnal.deviation.toFixed(2)} kPa` : ''}

### VPD moyen 24h
- **Valeur**: ${dailyIndicators.vpd?.mean24h || 'N/A'} kPa

### Amplitude VPD
- **Valeur**: ${dailyIndicators.vpd?.amplitude || 'N/A'} kPa

## 🌱 Humidité du Substrat sur 24h

### Humidité moyenne du substrat
- **Valeur**: ${dailyIndicators.humidity?.mean24h || 'N/A'}%
- **Statut**: ${dailyEvaluation.byIndicator?.soil_moisture ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.soil_moisture.severity)} ${dailyEvaluation.byIndicator.soil_moisture.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.soil_moisture?.threshold?.optimal || 'Non configurée'}
${dailyEvaluation.byIndicator?.soil_moisture?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.soil_moisture.deviation.toFixed(2)}%` : ''}

### Plage d'humidité du substrat
- **Minimum**: ${dailyIndicators.humidity?.min || 'N/A'}%
- **Maximum**: ${dailyIndicators.humidity?.max || 'N/A'}%
- **Amplitude**: ${dailyIndicators.humidity?.amplitude || 'N/A'}%
- **Impact**: L'amplitude indique la stabilité de l'irrigation

## 🌬️ CO2 (Dioxyde de carbone) sur 24h

### CO2 diurne moyen (6h-20h)
- **Valeur**: ${dailyIndicators.co2?.diurnalMean || 'N/A'} ppm
- **Statut**: ${dailyEvaluation.byIndicator?.co2_diurnal ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.co2_diurnal.severity)} ${dailyEvaluation.byIndicator.co2_diurnal.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.co2_diurnal?.threshold?.optimal || 'Non configurée'}
- **Impact**: Le CO2 est essentiel pour la photosynthèse. En serre fermée, le CO2 peut s'épuiser rapidement pendant les heures de lumière.
${dailyEvaluation.byIndicator?.co2_diurnal?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.co2_diurnal.deviation.toFixed(0)} ppm` : ''}

### Amplitude CO2 24h (épuisement jour/nuit)
- **Valeur**: ${dailyIndicators.co2?.amplitude || 'N/A'} ppm
- **Statut**: ${dailyEvaluation.byIndicator?.co2_amplitude ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.co2_amplitude.severity)} ${dailyEvaluation.byIndicator.co2_amplitude.message}` : 'Non évalué'}
- **Impact**: Une amplitude élevée (>400 ppm) indique un épuisement important du CO2 pendant les heures de photosynthèse active. Cela peut limiter la croissance et nécessiter un enrichissement en CO2.
${dailyEvaluation.byIndicator?.co2_amplitude?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.co2_amplitude.deviation.toFixed(0)} ppm au-dessus de la plage optimale` : ''}

### Saturation CO2 (durée exposition >1400 ppm)
- **Valeur**: ${dailyIndicators.co2?.saturationHours || 'N/A'} heures
- **Statut**: ${dailyEvaluation.byIndicator?.co2_saturation ? `${this._getStatusEmoji(dailyEvaluation.byIndicator.co2_saturation.severity)} ${dailyEvaluation.byIndicator.co2_saturation.message}` : 'Non évalué'}
- **Zone optimale**: ${dailyEvaluation.byIndicator?.co2_saturation?.threshold?.optimal || 'Non configurée'}
- **Impact critique**: Au-delà de 1400 ppm, la photosynthèse devient inefficace car les plantes ne peuvent plus absorber davantage de CO2
${dailyEvaluation.byIndicator?.co2_saturation?.deviation > 0 ? `- ⚠️ **Écart détecté**: ${dailyEvaluation.byIndicator.co2_saturation.deviation.toFixed(2)} heures` : ''}

### Plage CO2 complète
- **Minimum**: ${dailyIndicators.co2?.min || 'N/A'} ppm
- **Maximum**: ${dailyIndicators.co2?.max || 'N/A'} ppm
- **CO2 nocturne moyen**: ${dailyIndicators.co2?.nocturnalMean || 'N/A'} ppm

## 📊 Points de données collectés
- **Lumière**: ${dailyIndicators.light?.dataPoints || 0} mesures (couverture: ${dailyIndicators.light?.coverage || '0%'})
- **VPD**: ${dailyIndicators.vpd?.dataPoints || 0} mesures
- **Température**: ${dailyIndicators.temperature?.dataPoints || 0} mesures
- **Humidité substrat**: ${dailyIndicators.humidity?.dataPoints || 0} mesures
- **CO2**: ${dailyIndicators.co2?.dataPoints || 0} mesures

` : '⚠️ Indicateurs 24h non disponibles pour cette période'}

`}

${periodType === 'daily' ? `
---

# MESURES INSTANTANÉES (VALEURS PONCTUELLES)

## Indicateurs disponibles (${analyticsReport.indicators.length})

${analyticsReport.indicators.map(ind => {
  const indicatorData = aggregatedData ? aggregatedData[ind.name] : null;
  const isLightIndicator = ind.isLightIndicator || false;
  const meanLabel = isLightIndicator ? '**Moyenne diurne** (valeurs > 0 uniquement)' : '**Moyenne**';

  return `
### ${ind.name}
- **Unité**: ${ind.unit}
- **Points de données**: ${ind.dataPoints} (couverture: ${ind.coverage})
- **Plage observée**: ${ind.range.min} - ${ind.range.max} ${ind.unit}
- ${meanLabel}: ${ind.range.mean} ${ind.unit}
- **Dernière mesure**: ${indicatorData?.latest != null ? indicatorData.latest.toFixed(2) : 'N/A'} ${ind.unit}${indicatorData?.latestTimestamp ? ` (${new Date(indicatorData.latestTimestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})` : ''}
- **Tendance**: ${ind.trend}${indicatorData ? formatTimeSeries(indicatorData) : ''}
${isLightIndicator ? '\n*Note: Pour les indicateurs de lumière (radiation, PAR), la moyenne exclut les périodes nocturnes (valeurs à zéro) pour refléter l\'intensité réelle pendant les heures d\'ensoleillement.*' : ''}
`;
}).join('\n')}
` : ''}

---

# ÉVALUATION PAR RAPPORT AUX SEUILS OPTIMAUX

**Statut global**: ${this._getStatusEmoji(thresholdsEvaluation.overall.status)} ${thresholdsEvaluation.overall.status}

- ✅ Indicateurs optimaux: ${thresholdsEvaluation.overall.optimalCount}
- ⚠️ Indicateurs en avertissement: ${thresholdsEvaluation.overall.warningCount}
- 🔴 Indicateurs critiques: ${thresholdsEvaluation.overall.criticalCount}

## Détail par indicateur:

${Object.entries(thresholdsEvaluation.byIndicator).map(([name, evaluation]) => {
  const valueLabel = evaluation.isLightIndicator
    ? '**Valeur moyenne diurne**'
    : '**Valeur actuelle**';

  return `
### ${name}
- ${valueLabel}: ${evaluation.latestValue != null ? evaluation.latestValue.toFixed(2) : 'N/A'} ${evaluation.unit}
- **Statut**: ${this._getStatusEmoji(evaluation.severity)} ${evaluation.message}
- **Zone optimale**: ${evaluation.threshold?.optimal || 'Non configurée'}
${evaluation.deviation > 0 ? `- **Écart**: ${evaluation.deviation.toFixed(2)} ${evaluation.unit}` : ''}
`;
}).join('\n')}

---

# ANOMALIES DÉTECTÉES

${analyticsReport.anomalies.length > 0 ?
  analyticsReport.anomalies.map(a => `
- **${a.indicator}** (${a.timestamp}): ${a.description} [${a.severity.toUpperCase()}]
  `).join('\n') :
  'Aucune anomalie majeure détectée.'
}

---

# TA MISSION

En tant qu'expert agronome, rédige un rapport d'analyse professionnel, structuré et fluide.

**DÉMARCHE AGRONOMIQUE**:
- Analyse basée sur les **principes agronomiques** de physiologie végétale et climatologie des cultures
- **Objectif principal**: Optimiser la productivité de la culture (croissance, rendement, qualité)
- **Objectif secondaire**: Réduire les risques agronomiques (stress, maladies, pertes de rendement)
- Toutes recommandations doivent être évaluées selon leur **impact sur la productivité** et la **réduction des risques**

**CONTEXTE: SERRES AU MAROC - MOYENS ACTIONNABLES**:
Les recommandations doivent être RÉALISTES et ACTIONNABLES avec les moyens disponibles dans les serres marocaines :

- **Température** : Ventilation naturelle/forcée, ombrage (filets 30-50%), ouverture/fermeture ouvrants latéraux et zénithaux, bâches thermiques nocturnes, paillage sol. ❌ PAS de chauffage actif disponible.
- **CO2** : Ventilation pour évacuer excès, aération contrôlée. ❌ PAS d'enrichissement CO2 artificiel disponible.
- **Humidité/VPD** : Brumisation, micro-aspersion, ventilation, paillage, gestion irrigation goutte-à-goutte, bassines d'eau.
- **Lumière** : Filets d'ombrage amovibles (30-50% réduction), nettoyage plastique serre, orientation plants.
- **Substrat** : Ajustement fréquence/durée irrigation goutte-à-goutte, drainage, paillage.

${periodType === 'daily' ? `
**FOCUS D'ANALYSE : ALERTES TEMPS RÉEL**
- Base ton diagnostic sur les **dernières valeurs mesurées** (section "MESURES INSTANTANÉES")
- L'interface affiche uniquement les alertes critiques et warnings, ou "Tout normal" si aucune alerte
- Concentre-toi sur les **actions immédiates** à prendre pour les paramètres en alerte
- Ton analyse doit être **concise et actionnelle** : diagnostiquer rapidement et recommander des actions urgentes
- Compare les valeurs actuelles aux seuils optimaux et identifie les déviations préoccupantes

**MÉTHODOLOGIE D'ANALYSE (MODE ALERTES)**:

1. **Si AUCUNE alerte critique/warning**:
   - Diagnostic bref confirmant que toutes conditions sont optimales
   - Recommendations préventives légères (surveillance, anticipation météo)

2. **Si ALERTES présentes**:
   - Priorise par gravité: critiques d'abord, puis warnings
   - Pour chaque alerte, rédige un paragraphe fluide incluant:
     * Cause probable immédiate
     * Impact sur la culture dans les prochaines heures
     * Action corrective urgente et précise (ex: "Augmenter température à 15°C", "Activer brumisation")
     * Délai d'intervention intégré dans le texte (immédiat, dans l'heure, aujourd'hui)
` : `
**FOCUS D'ANALYSE : TENDANCES SUR 24H**
- Base ton diagnostic sur les **indicateurs agronomiques 24h** (DLI, **VPD diurne**, stress thermique, saturation CO2)
- Analyse les **tendances et amplitudes** sur la période
- Évalue la **cohérence des conditions** sur 24h (variations jour/nuit)
- Identifie les risques liés aux durées de stress (thermique, saturation CO2)
- **IMPORTANT VPD**: Utilise UNIQUEMENT le **VPD diurne** (dailyIndicators.vpd.diurnalMean), JAMAIS le VPD moyen 24h (la nuit VPD≈0 dilue l'information pertinente pour l'analyse agronomique)

**MÉTHODOLOGIE D'ANALYSE (IMPÉRATIF)**:

1. **Utilise la HIÉRARCHIE DE PRIORITÉS** définie ci-dessus:
   - 🔴 PRIORITÉ 1: Identifie d'abord les facteurs limitants (DLI < 12, VPD < 0.5 ou > 1.5, Stress thermique > 3h, Saturation CO2 > 3h)
   - 🟡 PRIORITÉ 2: Ensuite évalue les facteurs d'optimisation (VPD diurne optimal, DLI optimal)
   - 🟢 PRIORITÉ 3: Enfin les facteurs de performance fine

2. **Analyse les INTERACTIONS entre indicateurs avec nuance**:
   - Si VPD **diurne** élevé + stress thermique → Recommande ventilation/ombrage prioritaire puis brumisation pour ajuster humidité
   - Si DLI optimal + CO2 diurne bas → CO2 ambiant suffisant généralement, ventilation naturelle apporte renouvellement
   - Si DLI bas + CO2 diurne élevé → Prioriser augmentation lumière (nettoyage plastique, réduction ombrage), expliquer pourquoi CO2 est sous-utilisé
   - Si Stress thermique prolongé + Lumière élevée → Ventilation/ombrage urgent pour débloquer photosynthèse
   - Si Saturation CO2 + DLI élevé → Ventilation nécessaire pour évacuer excès, expliquer l'inefficacité photosynthétique

3. **STYLE DE RÉDACTION - CONCIS, STRUCTURÉ ET MESURÉ**:
   - Utilise des **sous-titres markdown (##)** pour structurer clairement l'analyse
   - Rédige des **paragraphes courts** (3-5 phrases maximum)
   - Style **factuel et direct** : chiffres + constat + conclusion, sans sur-développement
   - Format des valeurs : "DLI: 14,4 mol/m²/jour (optimal, plage 12-18)"
   - Connecteurs courts et efficaces : "Cependant", "De plus", "Concernant"
   - **Évite la verbosité** : pas de phrases redondantes ou explicatives inutiles
   - **TON MESURÉ ET PROFESSIONNEL** : Évite termes alarmistes ("graves retards", "catastrophique", "dramatique", "désastreux")
   - **QUANTIFIE les impacts** : Préfère "réduction de croissance de 15-20%", "ralentissement photosynthétique modéré", "risque modéré/élevé de stress"
   - **NUANCE** : Utilise "peut entraîner", "risque de", "susceptible de" au lieu d'affirmations catégoriques

4. **FORMAT DES RECOMMANDATIONS - STRUCTURÉ ET ACTIONNABLE**:
   Pour chaque recommandation, utilise ce format concis:

   Titre avec urgence en gras, puis paragraphe structuré avec:
   - Constat: Valeur mesurée (statut) + Impact direct
   - Action: Description précise avec valeur cible
   - Bénéfice: Gain attendu (quantifié si possible)

   Exemple de format attendu:
   "**[IMMÉDIAT] Augmentation température nocturne**

   Constat: 8,2°C mesuré (critique < 10°C). Arrêt de croissance et risque de dommages cellulaires.

   Action: Activer chauffage, régler thermostat à 13-14°C minimum.

   Bénéfice: Restauration croissance, évitement retard développement 7-10 jours."

5. **PRÉCAUTIONS - COHÉRENCE ET RÉALISME**:
   - **VPD: Utilise UNIQUEMENT VPD diurne** (dailyIndicators.vpd.diurnalMean), JAMAIS VPD 24h (nuit VPD≈0)
   - Vérifie cohérence valeurs/recommandations
   - Évite recommandations contradictoires
   - Contextualise selon culture et saison
   - Limites réalistes (pas de VPD > 2 kPa, température > 35°C)
   - Signale explicitement interactions entre facteurs

6. **ORGANISATION PAR PRIORITÉ**:
   - Synthèse globale (2-3 phrases)
   - Facteurs limitants (Priorité 1) avec sous-titres
   - Facteurs d'optimisation (Priorité 2) avec sous-titres
   - Points de surveillance (Priorité 3) concis
`}

**IMPORTANT**: Ta réponse DOIT être au format JSON structuré suivant (et uniquement du JSON valide, sans texte avant ou après):

\`\`\`json
{
  "diagnostic": "Paragraphe(s) de diagnostic global : vue d'ensemble de l'état de la culture, principaux constats (points forts et préoccupations), en style narratif fluide avec connecteurs logiques",
  "detailedAnalysis": "Analyse structurée par THÈMES AGRONOMIQUES avec sous-titres markdown (##). Chaque thème: 2-4 phrases courtes, valeurs intégrées, impact agronomique clair.

## Photosynthèse et Croissance
Analyser **Radiation moyenne diurne** + **DLI** + **Durée d'ensoleillement** + **CO2 diurne** + **Température diurne** + **VPD diurne** comme un SYSTÈME pour l'activité photosynthétique.

- **Radiation diurne** (µmol/m²/s) : Intensité lumineuse instantanée moyenne pendant journée. Lien avec nébulosité, saison.
- **DLI** (mol/m²/jour) : Cumul de lumière reçue sur 24h. Combine intensité × durée.
- **Durée ensoleillement** (heures) : Temps effectif d'exposition lumineuse. Complète l'analyse DLI (DLI bas + durée courte = ciel couvert vs DLI bas + durée longue = faible intensité).

Identifier synergies ou décalages entre ces facteurs. Impact sur croissance et productivité.

## Stress et Facteurs Limitants
Évaluer Stress thermique (durée >30°C) + Saturation CO2 (durée >1400ppm) + VPD diurne extrêmes (<0.5 ou >1.5 kPa). Identifier blocages physiologiques et risques immédiats pour la culture.

## Gestion de l'Eau et Substrat
Examiner Humidité substrat 24h + relation avec VPD diurne. Évaluer disponibilité hydrique et risques de stress hydrique ou excès.

## Conditions Nocturnes et Récupération
Analyser Température nocturne + VPD nocturne. Évaluer qualité du repos végétatif, respiration, et récupération énergétique pour le lendemain.",
  "recommendations": "Paragraphes de recommandations hiérarchisées : actions prioritaires d'abord, puis optimisations. Chaque recommandation intègre le problème, l'action concrète, le bénéfice attendu et l'urgence dans un texte fluide. Utilise des transitions pour relier les actions entre elles",
  "predictions": "Paragraphe(s) sur les risques et opportunités : surveillance recommandée, évolutions probables, points de vigilance, en style narratif avec nuances appropriées",
  "references": "Paragraphe listant les principes agronomiques et références techniques utilisés pour étayer les recommandations"
}
\`\`\`

**Important**:
- Réponds UNIQUEMENT avec du JSON valide (pas de texte avant ou après)
- Chaque champ doit contenir un texte **narratif fluide en paragraphes**, PAS de listes à puces
- Utilise le formatage markdown uniquement pour les emphases (gras, italique) et non pour structurer en listes
- Intègre les valeurs numériques naturellement dans le texte
- Utilise des connecteurs logiques entre les phrases et paragraphes
- Adopte un ton professionnel, nuancé et sans ambiguïté
- Exemple de bon style : "L'analyse des conditions lumineuses révèle une situation favorable. Le DLI mesuré atteint 14,4 mol/m²/jour, une valeur qui se situe dans la plage optimale pour la culture de framboises. Cette quantité de lumière favorise une photosynthèse efficace et ne nécessite aucun ajustement immédiat. En revanche, la gestion thermique nécessite une attention particulière..."`;
  }

  /**
   * Convertit un objet JSON en texte formaté en markdown
   * @param {Object|Array|string} obj - Objet à convertir
   * @param {number} level - Niveau de profondeur (pour l'indentation)
   * @returns {string} Texte formaté en markdown
   */
  _convertObjectToMarkdown(obj, level = 0) {
    if (!obj) return '';

    // Si c'est déjà une string, la retourner telle quelle
    if (typeof obj === 'string') {
      return obj;
    }

    // Si c'est un tableau
    if (Array.isArray(obj)) {
      return obj.map((item) => {
        if (typeof item === 'string') {
          return `- ${item}`;
        } else if (typeof item === 'object') {
          return this._convertObjectToMarkdown(item, level + 1);
        }
        return `- ${String(item)}`;
      }).join('\n');
    }

    // Si c'est un objet
    if (typeof obj === 'object') {
      const lines = [];

      Object.entries(obj).forEach(([key, value]) => {
        // Formatter la clé (remplacer les underscores par des espaces, capitaliser)
        const formattedKey = key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase());

        if (typeof value === 'string') {
          // Si la valeur est une string, l'afficher directement
          lines.push(`### ${formattedKey}\n\n${value}\n`);
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          // Si c'est un sous-objet, créer une section
          lines.push(`### ${formattedKey}\n`);
          Object.entries(value).forEach(([subKey, subValue]) => {
            const formattedSubKey = subKey
              .replace(/_/g, ' ')
              .replace(/\b\w/g, char => char.toUpperCase());
            lines.push(`**${formattedSubKey}**: ${String(subValue)}\n`);
          });
        } else if (Array.isArray(value)) {
          // Si c'est un tableau
          lines.push(`### ${formattedKey}\n`);
          lines.push(this._convertObjectToMarkdown(value, level + 1));
        } else {
          // Pour les autres types (number, boolean, etc.)
          lines.push(`**${formattedKey}**: ${String(value)}\n`);
        }
      });

      return lines.join('\n');
    }

    // Fallback: convertir en string
    return String(obj);
  }

  /**
   * Parse la réponse d'Aladinn au format JSON
   * @param {string} response - Réponse brute d'Aladinn
   * @returns {Object|null} Réponse structurée ou null si parsing échoue
   */
  _parseAladinnResponse(response) {
    if (!response) {
      console.error('❌ Réponse vide d\'Aladinn');
      return null;
    }

    try {
      // Nettoyer la réponse: extraire le JSON s'il est entouré de texte ou de code blocks
      let jsonText = response.trim();

      console.log('🔍 Tentative de parsing JSON (longueur:', jsonText.length, 'chars)');

      // Cas 1: JSON entouré de ```json ... ``` ou ``` ... ```
      const codeBlockMatch = jsonText.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
      if (codeBlockMatch) {
        console.log('✓ JSON trouvé dans un code block');
        jsonText = codeBlockMatch[1].trim();
      }

      // Cas 2: JSON pur - chercher le premier { jusqu'au dernier }
      // Amélioration: utiliser un compteur de brackets pour trouver le JSON complet
      const firstBrace = jsonText.indexOf('{');
      const lastBrace = jsonText.lastIndexOf('}');

      if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
        jsonText = jsonText.substring(firstBrace, lastBrace + 1);
        console.log('✓ JSON extrait entre brackets (longueur:', jsonText.length, ')');
      } else {
        console.warn('⚠️ Aucun JSON détecté avec { }');
      }

      // Nettoyer les caractères invisibles et problématiques
      jsonText = jsonText
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Supprimer caractères de contrôle
        .replace(/\\n/g, '\\n')  // Préserver les vraies nouvelles lignes dans les strings
        .trim();

      console.log('📝 JSON nettoyé, tentative de parsing...');

      // Parser le JSON
      const parsed = JSON.parse(jsonText);

      // Vérifier que les champs attendus sont présents
      if (parsed && typeof parsed === 'object') {
        console.log('✅ Réponse Aladinn parsée au format JSON avec succès');
        console.log('📋 Champs détectés:', Object.keys(parsed).join(', '));

        // Convertir les champs qui sont des objets en markdown
        const sections = {
          diagnostic: typeof parsed.diagnostic === 'string'
            ? parsed.diagnostic
            : (parsed.diagnostic ? this._convertObjectToMarkdown(parsed.diagnostic) : ''),
          detailedAnalysis: typeof parsed.detailedAnalysis === 'string'
            ? parsed.detailedAnalysis
            : (parsed.detailedAnalysis ? this._convertObjectToMarkdown(parsed.detailedAnalysis) : ''),
          recommendations: typeof parsed.recommendations === 'string'
            ? parsed.recommendations
            : (parsed.recommendations ? this._convertObjectToMarkdown(parsed.recommendations) : ''),
          predictions: typeof parsed.predictions === 'string'
            ? parsed.predictions
            : (parsed.predictions ? this._convertObjectToMarkdown(parsed.predictions) : ''),
          references: typeof parsed.references === 'string'
            ? parsed.references
            : (parsed.references ? this._convertObjectToMarkdown(parsed.references) : '')
        };

        console.log('✅ Sections converties (détails):');
        Object.entries(sections).forEach(([key, value]) => {
          const preview = value ? (value.length > 100 ? value.substring(0, 100) + '...' : value) : '(vide)';
          console.log(`  - ${key}: ${typeof value === 'string' ? 'string' : 'objet converti'} - ${preview}`);
        });

        return {
          sections,
          fullText: response
        };
      }

      console.error('❌ Format JSON invalide (objet non conforme)');
      console.error('Type reçu:', typeof parsed);
      console.error('Réponse brute (500 premiers chars):', response.substring(0, 500));
      return null;

    } catch (error) {
      console.error('❌ Échec du parsing JSON d\'Aladinn:', error.message);
      console.error('Position de l\'erreur:', error.message.match(/position (\d+)/)?.[1] || 'inconnue');
      console.error('Réponse brute (premiers 500 chars):', response.substring(0, 500));
      console.error('Réponse brute (derniers 500 chars):', response.substring(Math.max(0, response.length - 500)));

      // Tenter un dernier fallback: chercher du JSON valide n'importe où
      try {
        console.log('🔄 Tentative de fallback: recherche de JSON valide...');
        const jsonMatches = response.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g);
        if (jsonMatches && jsonMatches.length > 0) {
          console.log(`✓ Trouvé ${jsonMatches.length} candidat(s) JSON`);
          // Trier par taille décroissante et essayer de parser
          const sortedMatches = jsonMatches.sort((a, b) => b.length - a.length);
          for (const candidate of sortedMatches) {
            try {
              const parsed = JSON.parse(candidate);
              if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
                console.log('✅ Fallback réussi! JSON trouvé et parsé');
                // Réutiliser la même logique de conversion
                const sections = {
                  diagnostic: typeof parsed.diagnostic === 'string' ? parsed.diagnostic : (parsed.diagnostic ? this._convertObjectToMarkdown(parsed.diagnostic) : ''),
                  detailedAnalysis: typeof parsed.detailedAnalysis === 'string' ? parsed.detailedAnalysis : (parsed.detailedAnalysis ? this._convertObjectToMarkdown(parsed.detailedAnalysis) : ''),
                  recommendations: typeof parsed.recommendations === 'string' ? parsed.recommendations : (parsed.recommendations ? this._convertObjectToMarkdown(parsed.recommendations) : ''),
                  predictions: typeof parsed.predictions === 'string' ? parsed.predictions : (parsed.predictions ? this._convertObjectToMarkdown(parsed.predictions) : ''),
                  references: typeof parsed.references === 'string' ? parsed.references : (parsed.references ? this._convertObjectToMarkdown(parsed.references) : '')
                };
                return { sections, fullText: response };
              }
            } catch (e) {
              // Ignorer et essayer le suivant
              continue;
            }
          }
        }
      } catch (fallbackError) {
        console.error('❌ Fallback échoué:', fallbackError.message);
      }

      return null;
    }
  }

  /**
   * Retourne un emoji selon le statut
   */
  _getStatusEmoji(status) {
    const emojis = {
      'critical': '🔴',
      'warning': '⚠️',
      'optimal': '✅',
      'success': '✅',
      'acceptable': '🟢',
      'info': 'ℹ️',
      'unknown': '❓'
    };
    return emojis[status] || '•';
  }

  // =========================================================================
  // SAUVEGARDE DANS L'HISTORIQUE
  // =========================================================================

  /**
   * Sauvegarde l'analyse dans l'historique via l'API
   * @param {Object} analysisResult - Résultat complet de l'analyse
   * @param {Object} params - Paramètres originaux de l'analyse
   */
  async _saveAnalysisToHistory(analysisResult, params) {
    const {
      farmId,
      farmName,
      culture,
      objective,
      period,
      sectorId
    } = params;

    const { metadata, data, analysis } = analysisResult;

    // Extraire les sections parsées
    const parsed = analysis.parsed;
    if (!parsed || !parsed.sections) {
      console.warn('⚠️ Analyse non parsée, sauvegarde ignorée');
      return;
    }

    const { diagnostic, detailedAnalysis, recommendations, predictions, references } = parsed.sections;

    // Préparer le snapshot des indicateurs calculés
    const indicatorsSnapshot = {};
    if (data.dailyIndicators) {
      // Indicateurs de lumière
      if (data.dailyIndicators.light) {
        indicatorsSnapshot.dli = {
          value: data.dailyIndicators.light.dli,
          unit: 'mol/m²/jour'
        };
        indicatorsSnapshot.sunlight_duration = {
          value: data.dailyIndicators.light.sunlightDuration,
          unit: 'heures'
        };
        indicatorsSnapshot.radiation_mean_diurnal = {
          value: data.dailyIndicators.light.meanDiurnalRadiation,
          unit: 'µmol/m²/s'
        };
        indicatorsSnapshot.radiation_peak = {
          value: data.dailyIndicators.light.peakIntensity,
          unit: 'µmol/m²/s'
        };
      }

      // Indicateurs de température
      if (data.dailyIndicators.temperature) {
        indicatorsSnapshot.temperature_mean = {
          value: data.dailyIndicators.temperature.mean24h,
          unit: '°C'
        };
        indicatorsSnapshot.temperature_min = {
          value: data.dailyIndicators.temperature.min,
          unit: '°C'
        };
        indicatorsSnapshot.temperature_max = {
          value: data.dailyIndicators.temperature.max,
          unit: '°C'
        };
        indicatorsSnapshot.temperature_amplitude = {
          value: data.dailyIndicators.temperature.amplitude,
          unit: '°C'
        };
        indicatorsSnapshot.temperature_heat_stress = {
          value: data.dailyIndicators.temperature.heatStressHours,
          unit: 'heures'
        };
        indicatorsSnapshot.temperature_cold_stress = {
          value: data.dailyIndicators.temperature.coldStressHours,
          unit: 'heures'
        };
        indicatorsSnapshot.temperature_diurnal = {
          value: data.dailyIndicators.temperature.diurnalMean,
          unit: '°C'
        };
        indicatorsSnapshot.temperature_nocturnal = {
          value: data.dailyIndicators.temperature.nocturnalMean,
          unit: '°C'
        };
      }

      // Indicateurs VPD
      if (data.dailyIndicators.vpd) {
        indicatorsSnapshot.vpd_diurnal = {
          value: data.dailyIndicators.vpd.diurnalMean,
          unit: 'kPa'
        };
        indicatorsSnapshot.vpd_mean = {
          value: data.dailyIndicators.vpd.mean24h,
          unit: 'kPa'
        };
        indicatorsSnapshot.vpd_amplitude = {
          value: data.dailyIndicators.vpd.amplitude,
          unit: 'kPa'
        };
      }

      // Indicateurs d'humidité du substrat
      if (data.dailyIndicators.humidity) {
        indicatorsSnapshot.soil_moisture = {
          value: data.dailyIndicators.humidity.mean24h,
          unit: '%'
        };
        indicatorsSnapshot.soil_moisture_min = {
          value: data.dailyIndicators.humidity.min,
          unit: '%'
        };
        indicatorsSnapshot.soil_moisture_max = {
          value: data.dailyIndicators.humidity.max,
          unit: '%'
        };
        indicatorsSnapshot.soil_moisture_amplitude = {
          value: data.dailyIndicators.humidity.amplitude,
          unit: '%'
        };
      }

      // Indicateurs CO2
      if (data.dailyIndicators.co2) {
        indicatorsSnapshot.co2_diurnal = {
          value: data.dailyIndicators.co2.diurnalMean,
          unit: 'ppm'
        };
        indicatorsSnapshot.co2_amplitude = {
          value: data.dailyIndicators.co2.amplitude,
          unit: 'ppm'
        };
        indicatorsSnapshot.co2_saturation = {
          value: data.dailyIndicators.co2.saturationHours,
          unit: 'heures'
        };
        indicatorsSnapshot.co2_min = {
          value: data.dailyIndicators.co2.min,
          unit: 'ppm'
        };
        indicatorsSnapshot.co2_max = {
          value: data.dailyIndicators.co2.max,
          unit: 'ppm'
        };
        indicatorsSnapshot.co2_nocturnal = {
          value: data.dailyIndicators.co2.nocturnalMean,
          unit: 'ppm'
        };
      }

      // === DEBUG COMPLET : Trouver où se trouve airHumidity ===
      console.log('[DEBUG SAVE] Structure complète de data:', data);
      console.log('[DEBUG SAVE] data.dailyIndicators existe?', data.dailyIndicators ? 'OUI' : 'NON');
      console.log('[DEBUG SAVE] Clés disponibles dans dailyIndicators:', Object.keys(data.dailyIndicators || {}));
      console.log('[DEBUG SAVE] data.dailyIndicators.airHumidity:', data.dailyIndicators?.airHumidity);

      // Vérifier d'autres emplacements possibles
      console.log('[DEBUG SAVE] data.indicators:', data.indicators);
      console.log('[DEBUG SAVE] data.airHumidity:', data.airHumidity);

      // Indicateurs d'humidité relative de l'air
      if (data.dailyIndicators.airHumidity) {
        indicatorsSnapshot.humidity = {
          value: data.dailyIndicators.airHumidity.mean24h,
          unit: '%'
        };
        console.log('[DEBUG] indicatorsSnapshot.humidity sauvegardé:', indicatorsSnapshot.humidity);
      } else {
        console.log('[DEBUG] ❌ airHumidity MANQUANT - humidity non sauvegardé');
      }
    }

    // Préparer l'évaluation des seuils
    // IMPORTANT: Pour les analyses 24h, utiliser dailyEvaluation (indicateurs calculés sur 24h)
    // Sinon utiliser thresholdsEvaluation (indicateurs instantanés/agrégés)
    const useDaily = data.dailyEvaluation && Object.keys(data.dailyEvaluation.byIndicator || {}).length > 0;
    const thresholdsEvaluation = useDaily
      ? (data.dailyEvaluation?.byIndicator || {})
      : (data.thresholdsEvaluation?.byIndicator || {});

    // Calculer le statut global et les compteurs
    const evalSource = useDaily ? data.dailyEvaluation : data.thresholdsEvaluation;
    const overallStatus = evalSource?.overall?.status || 'unknown';
    const criticalIndicatorsCount = evalSource?.overall?.criticalCount || 0;
    const warningIndicatorsCount = evalSource?.overall?.warningCount || 0;
    const acceptableIndicatorsCount = evalSource?.overall?.acceptableCount || 0;
    const optimalIndicatorsCount = evalSource?.overall?.optimalCount || 0;

    // Listes des indicateurs critiques et en warning
    const criticalIndicatorsList = [];
    const warningIndicatorsList = [];

    Object.entries(thresholdsEvaluation).forEach(([indicator, evaluation]) => {
      if (evaluation.severity === 'critical') {
        criticalIndicatorsList.push(indicator);
      } else if (evaluation.severity === 'warning') {
        warningIndicatorsList.push(indicator);
      }
    });

    // Nombre de mesures utilisées (calculé depuis le summary d'agrégation)
    const summary = data.aggregation?.summary || {};
    const measurementsCount = (summary.points?.light || 0) +
                              (summary.points?.temperature || 0) +
                              (summary.points?.vpd || 0) +
                              (summary.points?.humidity || 0) +
                              (summary.points?.co2 || 0);

    // Données à envoyer à l'API
    const insightData = {
      farmId,
      farmName,
      sectorId: sectorId || null,
      sectorName: sectorId ? `Secteur ${sectorId}` : null,
      compartmentId: null, // Pas encore supporté
      compartmentName: null,
      culture,
      objective,
      analysisPeriod: period, // '24h', 'daily', 'weekly', 'monthly', '3months'
      startDate: metadata.period.start,
      endDate: metadata.period.end,
      indicatorsSnapshot,
      thresholdsEvaluation,
      measurementsCount,
      diagnostic,
      detailedAnalysis,
      recommendations,
      predictions,
      scientificReferences: references,
      rawAnalysis: analysis.raw,
      overallStatus,
      criticalIndicatorsCount,
      warningIndicatorsCount,
      acceptableIndicatorsCount,
      optimalIndicatorsCount,
      criticalIndicatorsList,
      warningIndicatorsList,
      aiModel: 'gpt-4', // Adapter selon le modèle utilisé
      promptTokens: null, // Non disponible pour l'instant
      completionTokens: null,
      analysisDurationMs: null
    };

    // Appeler l'API de sauvegarde
    await FarmRoadInsightsService.saveAnalysis(insightData);
  }

  // =========================================================================
  // UTILITAIRES
  // =========================================================================

  /**
   * Détermine les dates de début et fin selon la période
   * @param {string} period - Période sélectionnée
   * @param {Date} customStart - Date de début personnalisée (optionnel)
   * @param {Date} customEnd - Date de fin personnalisée (optionnel)
   * @returns {Object} Dates de début et fin
   */
  _determinePeriodDates(period, customStart = null, customEnd = null) {
    const end = customEnd || new Date();
    let start;

    switch (period) {
      case '24h':
        start = new Date(end);
        start.setHours(end.getHours() - 24);
        break;
      case 'daily':
        start = new Date(end);
        start.setHours(0, 0, 0, 0); // Début de la journée
        break;
      case 'weekly':
        start = new Date(end);
        start.setDate(end.getDate() - 7);
        break;
      case 'monthly':
        start = new Date(end);
        start.setMonth(end.getMonth() - 1);
        break;
      case '3months':
        start = new Date(end);
        start.setMonth(end.getMonth() - 3);
        break;
      case 'custom':
        start = customStart || new Date(end.setDate(end.getDate() - 7));
        break;
      default:
        // Par défaut, dernières 24h
        start = new Date(end);
        start.setHours(end.getHours() - 24);
    }

    return { start, end };
  }

  /**
   * Nettoie le cache temporaire de la session
   */
  clearCache() {
    this.analysisCache.clear();
    console.log('Cache d\'analyse vidé');
  }
}

// Export d'une instance singleton
export default new FarmRoadHybridService();
